import { useMemo, useState } from 'react';
import { ROUTES } from '@shared/api';
import type { Alarm, SuppressionWindow } from '@shared/domain';
import { useAuth } from '@/state/auth';
import {
  useAckAlarm,
  useAlarmHistory,
  useAlarmToWorkOrder,
  useAlarms,
  useCreateSuppression,
  useDeleteSuppression,
  useNotifications,
  useShelveAlarm,
  useSuppressions,
} from '@/lib/query';
import { LEVEL_LABEL, LEVEL_STYLE, ts } from '@/lib/format';
import { Badge, Card, DataTable, EmptyHint, SectionTitle } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';
import { Icon } from '@/components/layout/Icons';

type Tab = 'active' | 'history';

function SuppressionPanel() {
  const { user } = useAuth();
  const { data: wins, error } = useSuppressions();
  const create = useCreateSuppression();
  const del = useDeleteSuppression();
  const [form, setForm] = useState({ system: '', reason: '', hours: '2' });
  if (user?.role !== 'supervisor' && user?.role !== 'admin') {
    return <Card className="p-4 text-[13px] text-slate-500">维护窗口仅 supervisor / admin 可管理。</Card>;
  }
  if (error) return <Card className="p-4 text-[13px] text-slate-500">无权查看维护窗口。</Card>;
  const add = () => {
    const from = new Date().toISOString();
    const to = new Date(Date.now() + Number(form.hours || 1) * 3600_000).toISOString();
    create.mutate({ system: form.system || undefined, reason: form.reason || '计划维护', from, to });
    setForm({ system: '', reason: '', hours: '2' });
  };
  return (
    <Card className="p-5">
      <SectionTitle title="维护窗口（抑制告警）" desc="窗口内匹配的告警不推送、不计入活动视图。" />
      <div className="mb-3 flex flex-wrap gap-2">
        <input value={form.system} onChange={(e) => setForm({ ...form, system: e.target.value })} placeholder="系统(如 吊养)" className="rounded-lg border border-hairline px-3 py-2 text-sm" />
        <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="原因" className="rounded-lg border border-hairline px-3 py-2 text-sm" />
        <input value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} type="number" className="w-24 rounded-lg border border-hairline px-3 py-2 text-sm" />
        <button onClick={add} disabled={create.isPending} className="btn btn-dark">新增窗口</button>
      </div>
      <DataTable
        columns={[
          { key: 'system', header: '范围', render: (w: SuppressionWindow) => <span>{w.system ?? w.source ?? w.level ?? '全部'}</span> },
          { key: 'reason', header: '原因' },
          { key: 'from', header: '开始', render: (w: SuppressionWindow) => <span className="tabular-nums text-slate-500">{ts(w.from, false)}</span> },
          { key: 'to', header: '结束', render: (w: SuppressionWindow) => <span className="tabular-nums text-slate-500">{ts(w.to, false)}</span> },
          { key: 'ops', header: '操作', render: (w: SuppressionWindow) => <button onClick={() => del.mutate(w.id)} className="text-alarm-fg hover:underline">删除</button> },
        ]}
        rows={wins ?? []}
        empty={<EmptyHint>无维护窗口</EmptyHint>}
      />
    </Card>
  );
}

export default function AlarmCenter() {
  const { data: notif } = useNotifications();
  const { data: alarmData } = useAlarms();
  const { data: history } = useAlarmHistory();
  const ack = useAckAlarm();
  const shelve = useShelveAlarm();
  const toWo = useAlarmToWorkOrder();

  const [tab, setTab] = useState<Tab>('active');
  const [level, setLevel] = useState('all');
  const [kw, setKw] = useState('');
  const [sys, setSys] = useState('');

  const rows = (tab === 'active' ? alarmData?.active : history) ?? [];
  const filtered = useMemo(
    () =>
      rows.filter((a: Alarm) => {
        if (level !== 'all' && a.level !== level) return false;
        if (kw && !`${a.system} ${a.message}`.toLowerCase().includes(kw.toLowerCase())) return false;
        if (sys && !a.system.toLowerCase().includes(sys.toLowerCase())) return false;
        return true;
      }),
    [rows, level, kw, sys],
  );

  const doAck = (a: Alarm) => {
    const note = window.prompt(`确认告警「${a.message}」，可填处置说明（可留空）`) ?? undefined;
    ack.mutate({ id: a.id, note });
  };
  const doShelve = (a: Alarm) => {
    const m = window.prompt('搁置多少分钟？', '30');
    if (m) shelve.mutate({ id: a.id, minutes: Number(m) });
  };

  const columns = [
    { key: 'level', header: '级别', render: (a: Alarm) => <span className={`pill ${LEVEL_STYLE[a.level]}`}>{LEVEL_LABEL[a.level]}</span> },
    { key: 'ts', header: '时间', render: (a: Alarm) => <span className="tabular-nums text-slate-500">{ts(a.ts)}</span> },
    { key: 'system', header: '系统' },
    { key: 'message', header: '内容', render: (a: Alarm) => (
      <span>
        {a.count && a.count > 1 ? <Badge className="mr-2 bg-warn-bg text-warn-fg border-warn-border">×{a.count}</Badge> : null}
        {a.message}
        {a.lastSeen && a.count && a.count > 1 ? <span className="ml-2 text-[12px] text-slate-400">最近 {ts(a.lastSeen)}</span> : null}
      </span>
    ) },
    {
      key: 'wo',
      header: '工单状态',
      render: (a: Alarm) =>
        a.workOrderId ? (
          <div>
            <Badge className="bg-info-bg text-info-fg border-info-border">待指派</Badge>
            <div className="mt-1 text-[12px] text-slate-400">{a.workOrderId}</div>
          </div>
        ) : (
          <span className="text-slate-400">未转工单</span>
        ),
    },
    {
      key: 'ops',
      header: '操作',
      render: (a: Alarm) =>
        tab === 'history' ? (
          <span className="text-slate-400">
            {a.ackBy ? `${a.ackBy}${a.ackNote ? '：' + a.ackNote : ''}` : '—'}
          </span>
        ) : (
          <div className="flex items-center gap-3">
            <button onClick={() => doAck(a)} disabled={ack.isPending} className="text-slate-600 hover:text-brand-600">确认</button>
            <button onClick={() => doShelve(a)} disabled={shelve.isPending} className="text-slate-600 hover:text-brand-600">搁置</button>
            <button onClick={() => toWo.mutate(a.id)} disabled={toWo.isPending || !!a.workOrderId} className="rounded-md border border-hairline px-2 py-1 text-[13px] text-slate-600 hover:bg-slate-50 disabled:opacity-40">
              转工单
            </button>
          </div>
        ),
    },
  ];

  return (
    <div>
      <PageBanner />
      <SectionTitle title="通知投递日志" desc="告警 → 短信 / APP 推送双通道 fan-out 与回执" />
      <Card className="mb-8 p-0">
        <DataTable
          columns={[
            { key: 'ts', header: '时间', render: (n) => <span className="tabular-nums text-slate-500">{ts(n.ts)}</span> },
            { key: 'channel', header: '渠道', render: (n) => <span>{n.channel === 'sms' ? '短信' : 'APP推送'}</span> },
            { key: 'recipient', header: '接收人' },
            { key: 'status', header: '状态', render: (n) => <Badge className="bg-ok-bg text-ok-fg border-ok-border">{n.status}</Badge> },
            { key: 'content', header: '内容', render: (n) => <span className="block max-w-[560px] truncate font-mono text-[12px] text-slate-500" title={n.content}>{n.content}</span> },
          ]}
          rows={notif ?? []}
          empty={<EmptyHint>暂无通知</EmptyHint>}
        />
      </Card>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button onClick={() => setTab('active')} className={`btn ${tab === 'active' ? 'btn-dark' : 'btn-ghost'}`}>活动报警（{alarmData?.active.length ?? 0}）</button>
        <button onClick={() => setTab('history')} className={`btn ${tab === 'history' ? 'btn-dark' : 'btn-ghost'}`}>历史</button>
        <a href={ROUTES.alarms.exportCsv} className="btn btn-ghost"><Icon name="arrow" size={16} /> 导出活动 CSV</a>
        <a href="/work-orders" className="btn btn-ghost">工单看板 →</a>
        <span className="ml-2 text-[13px] text-slate-400">
          搁置 {alarmData?.shelved ?? 0} · 抑制 {alarmData?.suppressed ?? 0} · 已确认隐去 {alarmData?.acknowledgedHidden ?? 0}
        </span>
      </div>

      <Card className="mb-4 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="text-[13px] text-slate-500">级别
            <select value={level} onChange={(e) => setLevel(e.target.value)} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm">
              <option value="all">全部</option>
              <option value="L1">一级</option>
              <option value="L2">二级</option>
              <option value="L3">三级</option>
            </select>
          </label>
          <label className="text-[13px] text-slate-500">关键词
            <input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="救灾 / 纯氧 / …" className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" />
          </label>
          <label className="text-[13px] text-slate-500">系统
            <input value={sys} onChange={(e) => setSys(e.target.value)} placeholder="APP-救灾 / 源水 / …" className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" />
          </label>
          <div className="flex items-end gap-2">
            <button onClick={() => setTab(tab)} className="btn btn-dark">应用筛选</button>
            <button onClick={() => { setLevel('all'); setKw(''); setSys(''); }} className="btn btn-ghost">重置</button>
          </div>
        </div>
      </Card>

      <Card className="mb-8 p-0">
        <DataTable columns={columns} rows={filtered} empty={<EmptyHint>无匹配告警</EmptyHint>} />
      </Card>

      <SuppressionPanel />
    </div>
  );
}
